import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shirt, FileText, MessageSquare, Camera, BedDouble,
  ChevronRight, ChevronDown, Image, Loader2,
  RefreshCw, Clock, User, Building2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import useClienteActivo from '@/hooks/useClienteActivo';
import ClienteActivoSelector from '@/components/ClienteActivoSelector';
import { fetchEntregasClientes } from '@/services/entregasClientes.service';
import { getDownloadUrlOrOpen } from '@/services/documentsService';
import InformePanel from '../laundry/inventario-clientes/InformePanel';
import PqrsfPanel from '../laundry/inventario-clientes/PqrsfPanel';
import OcupacionPanel from '../laundry/inventario-clientes/OcupacionPanel';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const fmtDT = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ─────────────────────────────────────────────────────────────
// Feature Card
// ─────────────────────────────────────────────────────────────
const FeatureCard = ({ feature, isActive, onClick }) => {
  const Icon = feature.icon;
  return (
    <motion.div whileHover={{ y: feature.available ? -2 : 0 }} whileTap={{ scale: feature.available ? 0.98 : 1 }}>
      <Card
        onClick={onClick}
        className={`transition-all border-2 ${
          feature.available ? 'cursor-pointer' : 'cursor-default opacity-60'
        } ${
          isActive
            ? 'border-gray-900 shadow-lg'
            : 'border-transparent hover:border-gray-200 hover:shadow-md'
        }`}
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className={`${feature.iconBg} p-3 rounded-xl shrink-0`}>
              <Icon className={`h-6 w-6 ${feature.iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-gray-900">{feature.title}</p>
                {!feature.available && (
                  <Badge className="bg-amber-100 text-amber-700 text-xs shrink-0">Próximamente</Badge>
                )}
                {feature.available && (
                  isActive
                    ? <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                    : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1 leading-snug">{feature.description}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};


// ─────────────────────────────────────────────────────────────
// Evidencias Panel
// ─────────────────────────────────────────────────────────────
const EvidenciasPanel = ({ clienteId }) => {
  const [recolecciones, setRecolecciones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [evidenciaUrls, setEvidenciaUrls] = useState({});
  const [loadingEvidencia, setLoadingEvidencia] = useState({});

  const loadData = useCallback(async () => {
    if (!clienteId) return;
    setIsLoading(true);
    try {
      const res = await fetchEntregasClientes({
        query: { 'cliente.id': clienteId, tipo: 'Recoleccion', 'evidencias.0': { $exists: true } },
        limit: 200,
        page: 1,
        sort: { fecha_creacion: -1 },
      });
      setRecolecciones(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const loadEvidencia = useCallback(async (tx) => {
    if (!Array.isArray(tx.evidencias) || tx.evidencias.length === 0) return;
    if (evidenciaUrls[tx._id] !== undefined || loadingEvidencia[tx._id]) return;
    setLoadingEvidencia((prev) => ({ ...prev, [tx._id]: true }));
    try {
      const urls = await Promise.all(
        tx.evidencias.map((evId) =>
          getDownloadUrlOrOpen({ id: evId, openInNewTab: false }).catch(() => null),
        ),
      );
      setEvidenciaUrls((prev) => ({ ...prev, [tx._id]: urls.filter(Boolean) }));
    } catch {
      setEvidenciaUrls((prev) => ({ ...prev, [tx._id]: [] }));
    } finally {
      setLoadingEvidencia((prev) => ({ ...prev, [tx._id]: false }));
    }
  }, [evidenciaUrls, loadingEvidencia]);

  useEffect(() => {
    recolecciones.forEach((tx) => {
      if (expanded.has(tx._id)) loadEvidencia(tx);
    });
  }, [expanded]); // eslint-disable-line

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="h-5 w-5 text-purple-600" />
              Evidencias Fotográficas
            </CardTitle>
            <CardDescription className="mt-0.5">
              Registro fotográfico de prendas manchadas o desgastadas en recolecciones
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} disabled={isLoading} className="shrink-0">
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
            <p className="text-sm text-gray-400 mt-2">Cargando evidencias...</p>
          </div>
        ) : recolecciones.length === 0 ? (
          <div className="text-center py-12">
            <Image className="h-12 w-12 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-500 font-medium">Sin evidencias fotográficas registradas</p>
            <p className="text-xs text-gray-400 mt-1">Las fotos de prendas manchadas o desgastadas aparecerán aquí</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-gray-400 mb-3">{recolecciones.length} recolección{recolecciones.length !== 1 ? 'es' : ''} con evidencias</p>
            {recolecciones.map((tx) => {
              const isExpanded = expanded.has(tx._id);
              const items = tx.items || [];
              const totalItems = items.reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
              return (
                <div key={tx._id} className="border rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggle(tx._id)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                      <Camera className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-gray-800">{tx.consecutivo}</span>
                        <Badge className="bg-purple-100 text-purple-700 text-xs border-0">
                          {tx.evidencias.length} foto{tx.evidencias.length !== 1 ? 's' : ''}
                        </Badge>
                        <span className="text-xs text-gray-400">{totalItems} prendas</span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {fmtDT(tx.fecha_recoleccion || tx.fecha_creacion)}
                        </span>
                        {tx.conductor?.nombre && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {tx.conductor.nombre}
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-5 pt-3 bg-gray-50 border-t space-y-4">
                      {loadingEvidencia[tx._id] ? (
                        <div className="flex items-center gap-2 py-2 text-sm text-gray-400">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Cargando fotos...
                        </div>
                      ) : (
                        <>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                              Evidencias fotográficas
                            </p>
                            <div className="flex gap-3 flex-wrap">
                              {(evidenciaUrls[tx._id] || []).map((url, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => window.open(url, '_blank')}
                                  className="rounded-xl overflow-hidden border-2 border-white hover:border-purple-400 hover:shadow-lg transition-all"
                                >
                                  <img
                                    src={url}
                                    alt={`Evidencia ${idx + 1}`}
                                    className="w-28 h-28 object-cover"
                                  />
                                </button>
                              ))}
                              {evidenciaUrls[tx._id]?.length === 0 && (
                                <p className="text-sm text-gray-400 py-2">No se pudieron cargar las fotos</p>
                              )}
                            </div>
                          </div>

                          {items.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Prendas recolectadas
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {items.map((item, idx) => (
                                  <span
                                    key={idx}
                                    className="bg-white border border-gray-200 rounded-full px-3 py-1 text-xs text-gray-700"
                                  >
                                    {item.nombre} <strong className="text-gray-900">({item.cantidad_alistada || 0})</strong>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// ClientePrendasDashboard
// ─────────────────────────────────────────────────────────────
const FEATURES = [
  {
    id: 'informe',
    title: 'Informe de Prendas',
    description: 'Entregas y recolecciones, kardex detallado y consolidado por familia de prenda.',
    icon: FileText,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    available: true,
  },
  {
    id: 'pqrsf',
    title: 'PQRSF',
    description: 'Peticiones, Quejas, Reclamos, Sugerencias y Felicitaciones. ',
    icon: MessageSquare,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    available: true,
  },
  {
    id: 'evidencias',
    title: 'Evidencias Fotográficas',
    description: 'Registro fotográfico de prendas manchadas o desgastadas recibidas en recolecciones.',
    icon: Camera,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    available: true,
  },
  {
    id: 'ocupacion',
    title: 'Ocupación de Habitaciones',
    description: 'Registra y consulta la ocupación diaria de habitaciones de tu establecimiento.',
    icon: BedDouble,
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    available: true,
  },
];

const ClientePrendasDashboard = () => {
  const { user } = useAuth();
  const {
    clientesAsignados,
    clienteActivoId,
    clienteActivoNombre,
    clienteActivoNit,
    setClienteActivo,
  } = useClienteActivo();

  const [activeFeature, setActiveFeature] = useState(null);

  const handleFeatureClick = (id) => {
    if (!FEATURES.find((f) => f.id === id)?.available) return;
    setActiveFeature((prev) => (prev === id ? null : id));
  };

  const sinClientes = clientesAsignados.length === 0;
  const headerSubtitle = sinClientes
    ? 'Sin clientes asignados'
    : (clienteActivoNombre || user?.name || 'Cliente');

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b shadow-sm px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-100 p-2 rounded-xl shrink-0">
            <Shirt className="h-6 w-6 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900">Portal del Cliente</h1>
            <p className="text-sm text-gray-500 truncate">{headerSubtitle}</p>
          </div>
          {!sinClientes && clientesAsignados.length > 1 && (
            <ClienteActivoSelector
              clientes={clientesAsignados}
              activoId={clienteActivoId}
              onSelect={setClienteActivo}
            />
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 space-y-6">
        {sinClientes ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Building2 className="h-14 w-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">No tienes clientes asignados</p>
              <p className="text-sm text-gray-500 mt-1">
                Pide al administrador que te asigne uno o varios clientes en la parametrización de usuarios.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Feature Cards */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Funcionalidades disponibles</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {FEATURES.map((feature) => (
                  <FeatureCard
                    key={feature.id}
                    feature={feature}
                    isActive={activeFeature === feature.id}
                    onClick={() => handleFeatureClick(feature.id)}
                  />
                ))}
              </div>
            </div>

            {/* Feature Content */}
            <AnimatePresence mode="wait">
              {activeFeature === 'informe' && (
                <motion.div
                  key="informe"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <InformePanel clienteId={clienteActivoId} clienteNombre={clienteActivoNombre} />
                </motion.div>
              )}

              {activeFeature === 'pqrsf' && (
                <motion.div
                  key="pqrsf"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <PqrsfPanel
                    clienteId={clienteActivoId}
                    clienteNombre={clienteActivoNombre}
                    clienteNit={clienteActivoNit}
                  />
                </motion.div>
              )}

              {activeFeature === 'evidencias' && (
                <motion.div
                  key="evidencias"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <EvidenciasPanel clienteId={clienteActivoId} />
                </motion.div>
              )}

              {activeFeature === 'ocupacion' && (
                <motion.div
                  key="ocupacion"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <OcupacionPanel clienteId={clienteActivoId} clienteNombre={clienteActivoNombre} />
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientePrendasDashboard;
